// LUMINA · pages/vajra/admin/PractitionersSection.jsx
// Phase 112: Practitioner management — list + detail + credential CRUD

import React, { useState, useEffect, useCallback } from 'react';
import { API, GD, GDB, DG, CR, TXT, TXT2, PANEL, CRED_KEYS, CRED_TYPE_LABELS, CRED_TYPE_COLORS } from '../constants';

export default function PractitionersSection({ token, headers }) {
  const [members, setMembers]       = useState([]);
  const [search, setSearch]         = useState("");
  const [selected, setSelected]     = useState(null);  // {id, name, ...}
  const [profile, setProfile]       = useState(null);  // full profile
  const [loading, setLoading]       = useState(false);
  const [msg, setMsg]               = useState("");
  // Credential form
  const [showCredForm, setShowCredForm] = useState(false);
  const [credForm, setCredForm] = useState({credential_key:"", credential_type:"empowerment", notes:"", initiation_date:"", initiation_place:""});
  const [editingCredId, setEditingCredId] = useState(null);
  const [editCredForm, setEditCredForm] = useState({notes:"", credential_type:""});

  const loadMembers = useCallback(async () => {
    const d = await fetch(`${API}/vajra/staff/members`, {headers})
      .then(r => r.ok ? r.json() : {members:[]}).catch(() => ({members:[]}));
    setMembers(Array.isArray(d.members) ? d.members : []);
  }, [token]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const selectMember = async (m) => {
    setSelected(m); setProfile(null); setMsg(""); setShowCredForm(false); setEditingCredId(null);
    setLoading(true);
    const d = await fetch(`${API}/vajra/staff/members/${m.id}/profile`, {headers})
      .then(r => r.ok ? r.json() : null).catch(() => null);
    setProfile(d); setLoading(false);
  };

  const grantCred = async () => {
    if (!credForm.credential_key) { setMsg("请选择资质"); return; }
    const r = await fetch(`${API}/vajra/staff/members/${selected.id}/credentials`, {
      method: "POST", headers,
      body: JSON.stringify({...credForm, granted_by: null}),
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { setMsg("✓ 资质已添加"); setShowCredForm(false); setCredForm({credential_key:"",credential_type:"empowerment",notes:"",initiation_date:"",initiation_place:""}); selectMember(selected); }
    else setMsg("✗ " + (d.detail || "添加失败"));
  };

  const deleteCred = async (credId) => {
    if (!window.confirm("确认撤销该资质？")) return;
    const r = await fetch(`${API}/vajra/staff/members/${selected.id}/credentials/${credId}`, {method:"DELETE", headers});
    if (r.ok) { setMsg("✓ 已撤销"); selectMember(selected); }
    else setMsg("✗ 撤销失败");
  };

  const saveEditCred = async (credId) => {
    const r = await fetch(`${API}/vajra/staff/members/${selected.id}/credentials/${credId}`, {
      method: "PUT", headers,
      body: JSON.stringify(editCredForm),
    });
    if (r.ok) { setMsg("✓ 已更新"); setEditingCredId(null); selectMember(selected); }
    else setMsg("✗ 更新失败");
  };

  const deactivateMember = async () => {
    if (!window.confirm(`确认停用 ${selected.name} 的账号？此操作为软删除，可恢复。`)) return;
    const r = await fetch(`${API}/vajra/staff/members/${selected.id}`, {method:"DELETE", headers});
    if (r.ok) { setMsg("✓ 账号已停用"); setSelected(null); setProfile(null); loadMembers(); }
    else setMsg("✗ 操作失败");
  };

  const filtered = members.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (m.name||"").toLowerCase().includes(q) || (m.email||"").toLowerCase().includes(q) || (m.dharma_name||"").toLowerCase().includes(q);
  });

  const inputStyle = {width:"100%",padding:"7px 10px",borderRadius:7,border:"1px solid rgba(212,175,55,0.35)",background:"#fff",color:TXT,fontSize:13,outline:"none",boxSizing:"border-box"};
  const labelStyle = {fontSize:11,color:TXT2,letterSpacing:.5,marginBottom:3,display:"block"};

  return (
    <div style={{display:"flex",gap:20,alignItems:"flex-start",minHeight:400}}>
      {/* ── Left: Member List ── */}
      <div style={{width:240,flexShrink:0}}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="搜索成员…"
          style={{...inputStyle,marginBottom:10}}/>
        <div style={{fontSize:11,color:"#bbb",marginBottom:8}}>{filtered.length} 位成员</div>
        <div style={{maxHeight:480,overflowY:"auto"}}>
          {filtered.map(m => (
            <div key={m.id} onClick={() => selectMember(m)} style={{
              padding:"9px 12px",borderRadius:8,marginBottom:4,cursor:"pointer",
              background:selected?.id===m.id?"rgba(212,175,55,0.12)":"rgba(0,0,0,0.02)",
              border:`1px solid ${selected?.id===m.id?"rgba(212,175,55,0.4)":"rgba(0,0,0,0.06)"}`,
            }}>
              <div style={{fontSize:13,fontWeight:600,color:m.is_active!==false?TXT:"#ccc"}}>{m.name}{m.dharma_name&&<span style={{fontSize:11,color:GD,marginLeft:6}}>({m.dharma_name})</span>}</div>
              <div style={{fontSize:11,color:"#aaa",marginTop:2}}>{m.email}</div>
              {m.is_active===false&&<span style={{fontSize:10,color:"#e57373",border:"1px solid #e57373",borderRadius:4,padding:"1px 5px"}}>停用</span>}
            </div>
          ))}
          {filtered.length===0&&<div style={{textAlign:"center",padding:"20px 0",color:"#ccc",fontSize:13}}>暂无成员</div>}
        </div>
      </div>

      {/* ── Right: Member Detail ── */}
      <div style={{flex:1,minWidth:0}}>
        {!selected && (
          <div style={{textAlign:"center",padding:"60px 0",color:"#ccc",fontSize:13}}>← 选择左侧成员查看详情</div>
        )}

        {selected && (
          <div>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <div style={{fontSize:16,fontWeight:600,color:TXT}}>{selected.name}</div>
                {selected.dharma_name&&<div style={{fontSize:12,color:GD}}>法名: {selected.dharma_name}</div>}
                <div style={{fontSize:12,color:"#aaa"}}>{selected.email}</div>
              </div>
              <button onClick={deactivateMember} style={{
                fontSize:11,padding:"5px 12px",borderRadius:7,cursor:"pointer",
                background:"rgba(107,26,26,0.06)",color:"#e57373",border:"1px solid rgba(231,74,74,0.3)"}}>
                停用账号
              </button>
            </div>

            {msg&&<div style={{fontSize:12,padding:"7px 10px",borderRadius:6,marginBottom:12,
              background:msg.startsWith("✓")?"rgba(6,78,59,0.07)":"rgba(107,26,26,0.07)",
              color:msg.startsWith("✓")?DG:CR}}>{msg}</div>}

            {loading&&<div style={{textAlign:"center",padding:"20px",color:"#ccc"}}>加载中…</div>}

            {!loading && profile && (
              <div>
                {/* ── Credentials ── */}
                <div style={{marginBottom:20}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div style={{fontSize:12,color:GD,fontWeight:600,letterSpacing:.5}}>修行资质 ({profile.credentials?.length||0})</div>
                    <button onClick={()=>{setShowCredForm(s=>!s);setMsg("");}} style={{
                      fontSize:11,padding:"4px 12px",borderRadius:7,cursor:"pointer",
                      background:"rgba(212,175,55,0.08)",color:GD,border:`1px solid rgba(212,175,55,0.3)`}}>
                      {showCredForm?"取消":"＋ 添加资质"}
                    </button>
                  </div>

                  {showCredForm && (
                    <div style={{background:"rgba(212,175,55,0.04)",borderRadius:8,padding:14,marginBottom:12,border:"1px solid rgba(212,175,55,0.15)"}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                        <div>
                          <label style={labelStyle}>资质 *</label>
                          <select value={credForm.credential_key} onChange={e=>setCredForm(p=>({...p,credential_key:e.target.value}))} style={inputStyle}>
                            <option value="">— 选择 —</option>
                            {CRED_KEYS.map(k=><option key={k.key} value={k.key}>{k.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>类型</label>
                          <select value={credForm.credential_type} onChange={e=>setCredForm(p=>({...p,credential_type:e.target.value}))} style={inputStyle}>
                            <option value="empowerment">灌顶</option>
                            <option value="in_progress">修习中</option>
                            <option value="completed">已圆满</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>灌顶日期</label>
                          <input type="date" value={credForm.initiation_date} onChange={e=>setCredForm(p=>({...p,initiation_date:e.target.value}))} style={inputStyle}/>
                        </div>
                        <div>
                          <label style={labelStyle}>灌顶地点</label>
                          <input value={credForm.initiation_place} onChange={e=>setCredForm(p=>({...p,initiation_place:e.target.value}))} style={inputStyle} placeholder="如：宗果寺"/>
                        </div>
                      </div>
                      <div style={{marginBottom:10}}>
                        <label style={labelStyle}>备注</label>
                        <input value={credForm.notes} onChange={e=>setCredForm(p=>({...p,notes:e.target.value}))} style={inputStyle} placeholder="内部备注"/>
                      </div>
                      <button onClick={grantCred} style={{
                        padding:"7px 20px",background:`linear-gradient(135deg,${GD},#A07828)`,
                        border:"none",borderRadius:7,color:"#fff",fontSize:12,cursor:"pointer"}}>
                        添加资质
                      </button>
                    </div>
                  )}

                  {(profile.credentials||[]).map(c => (
                    <div key={c.id} style={{
                      display:"flex",alignItems:"center",justifyContent:"space-between",
                      padding:"8px 12px",borderRadius:7,marginBottom:6,
                      background:"rgba(0,0,0,0.02)",border:"1px solid rgba(0,0,0,0.06)"}}>
                      {editingCredId===c.id ? (
                        <div style={{flex:1,display:"flex",gap:8,alignItems:"center"}}>
                          <select value={editCredForm.credential_type} onChange={e=>setEditCredForm(p=>({...p,credential_type:e.target.value}))}
                            style={{...inputStyle,width:120}}>
                            <option value="empowerment">灌顶</option>
                            <option value="in_progress">修习中</option>
                            <option value="completed">已圆满</option>
                          </select>
                          <input value={editCredForm.notes} onChange={e=>setEditCredForm(p=>({...p,notes:e.target.value}))}
                            style={{...inputStyle,flex:1}} placeholder="备注"/>
                          <button onClick={()=>saveEditCred(c.id)} style={{fontSize:11,padding:"4px 10px",borderRadius:6,cursor:"pointer",background:"rgba(212,175,55,0.1)",color:GD,border:`1px solid rgba(212,175,55,0.3)`}}>保存</button>
                          <button onClick={()=>setEditingCredId(null)} style={{fontSize:11,padding:"4px 8px",borderRadius:6,cursor:"pointer",background:"transparent",color:"#999",border:"1px solid #e5e7eb"}}>取消</button>
                        </div>
                      ) : (
                        <>
                          <div style={{flex:1}}>
                            <span style={{fontSize:12,fontWeight:500,color:TXT}}>{c.label_zh||c.credential_key}</span>
                            <span style={{
                              fontSize:10,marginLeft:8,padding:"1px 7px",borderRadius:8,
                              background:`${CRED_TYPE_COLORS[c.credential_type]||"#999"}18`,
                              color:CRED_TYPE_COLORS[c.credential_type]||"#999",
                              border:`1px solid ${CRED_TYPE_COLORS[c.credential_type]||"#999"}44`,
                            }}>{CRED_TYPE_LABELS[c.credential_type]||c.credential_type}</span>
                            {c.granted_at&&<span style={{fontSize:10,color:"#bbb",marginLeft:8}}>{c.granted_at?.slice(0,10)}</span>}
                            {c.notes&&<div style={{fontSize:10,color:"#aaa",marginTop:2}}>{c.notes}</div>}
                          </div>
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>{setEditingCredId(c.id);setEditCredForm({notes:c.notes||"",credential_type:c.credential_type||"empowerment"});}} style={{fontSize:10,padding:"2px 8px",borderRadius:5,cursor:"pointer",background:"transparent",color:GD,border:`1px solid rgba(212,175,55,0.3)`}}>编辑</button>
                            <button onClick={()=>deleteCred(c.id)} style={{fontSize:10,padding:"2px 8px",borderRadius:5,cursor:"pointer",background:"transparent",color:"#e57373",border:"1px solid rgba(231,74,74,0.3)"}}>撤销</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {(profile.credentials||[]).length===0&&!showCredForm&&(
                    <div style={{fontSize:12,color:"#ccc",textAlign:"center",padding:"12px 0"}}>暂无资质记录</div>
                  )}
                </div>

                {/* ── Registration History ── */}
                {(profile.registrations||[]).length > 0 && (
                  <div>
                    <div style={{fontSize:12,color:GD,fontWeight:600,letterSpacing:.5,marginBottom:10}}>
                      报名历史 ({profile.registrations.length})
                    </div>
                    {profile.registrations.slice(0,10).map(r=>(
                      <div key={r.reg_id} style={{padding:"7px 10px",borderRadius:6,marginBottom:4,background:"rgba(0,0,0,0.02)",border:"1px solid rgba(0,0,0,0.05)"}}>
                        <div style={{fontSize:12,color:TXT}}>{r.event_title}</div>
                        <div style={{fontSize:10,color:"#bbb"}}>{r.event_type} · {r.event_date||"日期未定"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
